import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { GetServicesResponse } from '@kemu-io/hs-types';
import { HubConnectionStatus, HubConnectorWithWebAppMethods } from '@src/types/kemuHub/hub_t';

type GetServicesResponseInstance = GetServicesResponse & {
	isDefault?: boolean;
}

const getDefaultServicesResponse = (): GetServicesResponseInstance => ({
  available: [],
  installed: [],
  failed: [],
	isDefault: true,
});

/**
 * Uses the provided hub connection to fetch a list of services running on the hub.
 * It also responds to `service-change` commands and refreshes the list of services.
 * If the hub connection is not available, it will return an empty list.
 */
const useHubServices = (connector: HubConnectorWithWebAppMethods, status: HubConnectionStatus) => {
  const online = status === 'connected' || status === 'acknowledged';
	const [isLoading, setIsLoading] = useState(false);
  const [runningServices, setRunningServices] = useState<GetServicesResponseInstance>(getDefaultServicesResponse());
  const isAcknowledged = status === 'acknowledged';

	const onServicesChanged = useDebouncedCallback(async (updatedList: GetServicesResponse) => {
		setRunningServices(updatedList);
	}, 100);

	const fetchNewServices = useDebouncedCallback(async (bypassCache = false) => {
		const alreadyCached = connector.areServicesCached();
		let services: GetServicesResponse;

		if (!alreadyCached || bypassCache) {
			try {
				setIsLoading(true);
				services = (await connector.getServices());
				setIsLoading(false);
			} catch (e) {
				console.error('Error fetching services:', e);
				setIsLoading(false);
				return;
			}
		} else {
			services = connector.getCachedServices();
			setIsLoading(false);
		}

		setRunningServices({ ...services, isDefault: false });
	}, 100);

  useEffect(() => {

    const removeSubscription = connector.onCommand('services-changed', onServicesChanged);

		if (isAcknowledged) {
			fetchNewServices();
		} else {
      setRunningServices(c => c.available.length !== 0 ? getDefaultServicesResponse() : c);
    }

		return () => {
			onServicesChanged.cancel();
			fetchNewServices.cancel();
      removeSubscription();
		};
	}, [
		connector,
		status, // < == force reloading services if the connection status changes (as opposed to just the acknowledgement status)
		isAcknowledged,
		isLoading,
		onServicesChanged,
		fetchNewServices,
	]);

	const forceRefresh = useCallback(() => {
		fetchNewServices(true);
	}, [fetchNewServices]);

	// const memoizedServices = useMemo(() => ({
	// 	online,
	// 	services:	runningServices,
	// 	/**
	// 	 * Whether the services list has been fully loaded.
	// 	 */
	// 	isReady: !runningServices.isDefault && !isLoading,
	// 	/**
	// 	 * A function to force a refresh of the services list, bypassing the cache.
	// 	 */
	// 	forceRefresh: () => fetchNewServices(true),
	// }), [runningServices, fetchNewServices, online, isLoading]);

	return {
		online,
		services: runningServices,
		isReady: !runningServices.isDefault && !isLoading,
		forceRefresh,
	};
};

export default useHubServices;
